import styled from '@emotion/styled';
import { Box } from '@mui/material';
import { useMenu } from '@src/providers/MenuProvider';
import { useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';

interface Props {
  pdfUrl: string | undefined;
  rotation?: number;
  pageNumber?: number;
  zoomLevel: number;
  heightReduction?: number;
  setNumberOfPages?(pages: number): void;
}

type DocumentLoadSuccessParams = {
  numPages: number;
};

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const StyledDocument = styled(Document)({
  display: 'block',
  margin: '0 auto',
});

const PdfViewer = ({
  pdfUrl,
  rotation,
  pageNumber,
  zoomLevel,
  heightReduction = 0,
  setNumberOfPages,
}: Props) => {
  const { menuWidth } = useMenu();
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [currentRotation, setCurrentRotation] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);

  const isDragging = useRef(false);
  const startCoords = useRef({ x: 0, y: 0 });
  const scrollCoords = useRef({ left: 0, top: 0 });

  useEffect(() => {
    setCurrentRotation(rotation || 0);
  }, [rotation]);

  useEffect(() => {
    if (pageNumber) {
      setCurrentPageNumber(pageNumber);
    }
  }, [pageNumber]);

  const onDocumentLoadSuccess = ({ numPages }: DocumentLoadSuccessParams) => {
    if (setNumberOfPages) {
      setNumberOfPages(numPages);
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (containerRef.current) {
      isDragging.current = true;
      startCoords.current = { x: e.clientX, y: e.clientY };
      scrollCoords.current = {
        left: containerRef.current.scrollLeft,
        top: containerRef.current.scrollTop,
      };
      containerRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging.current && containerRef.current) {
      const deltaX = e.clientX - startCoords.current.x;
      const deltaY = e.clientY - startCoords.current.y;

      containerRef.current.scrollLeft = scrollCoords.current.left - deltaX;
      containerRef.current.scrollTop = scrollCoords.current.top - deltaY;
    }
  };

  const handleMouseUp = () => {
    if (isDragging.current && containerRef.current) {
      isDragging.current = false;
      containerRef.current.style.cursor = 'grab';
    }
  };

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'flex-start',
        backgroundColor: 'black',
        padding: theme.spacing(2),
        height: `calc(100vh - ${theme.spacing(28)} - ${theme.spacing(
          heightReduction
        )})`,
        width: `calc(100vw - ${menuWidth}px - ${theme.spacing(2)})`,
        overflow: 'auto',
        boxSizing: 'border-box',
        margin: '0 auto',
      })}
      ref={containerRef}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}>
      <StyledDocument file={pdfUrl} onLoadSuccess={onDocumentLoadSuccess}>
        <Page
          pageNumber={currentPageNumber}
          rotate={currentRotation}
          scale={zoomLevel}
          renderTextLayer={false}
          renderAnnotationLayer={false}
          renderMode="canvas"
        />
      </StyledDocument>
    </Box>
  );
};

export default PdfViewer;
